import { Direction } from '../sim/direction';
import type { EquipLayer } from '../sim/content';

/**
 * The draw-order vocabulary of a character's render stack: the base `body` plus
 * every {@link EquipLayer} slot. A {@link PlayerSprite} is an ordered stack of
 * these, and the order is **facing-dependent** - exactly as UO composites a
 * clothed mobile, where the cloak swaps across the body as the character turns.
 */
export type RenderLayer = 'body' | 'mount' | EquipLayer;

/**
 * The bottom-to-top draw order of everything **except the cloak**, which is the
 * only layer UO repositions by facing (confirmed against ClassicUO - see below).
 * The cloak is spliced into this base at a facing-dependent slot by
 * {@link cloakInsertBefore}; every other layer keeps this fixed relative order across
 * all 8 facings.
 *
 * Ported from ClassicUO's paperdoll/mobile compositor (a pure painter's algorithm,
 * later index = drawn on top): the default "arms early" table `PaperdollOrder.T2`
 * at `ClassicUO/src/ClassicUO.Client/Game/Data/PaperdollOrder.cs:39-46`, whose
 * shared-slot order britbank follows - leg garments (`pants`) with `feet` (shoes)
 * **over** the trouser legs, the torso garments (`shirt`/`doublet`), the `sash`
 * (UO Waist), `dress`, then `robe` (UO OuterTorso), and the head layers
 * (`hair`/`beard`/`headgear` = UO Helmet) topmost. The mapping of each britbank
 * cosmetic slot to its UO {@link https://github.com/ClassicUO/ClassicUO Layer}
 * enum value (`Game/Data/Layers.cs:5-37`) and britbank's own deliberate divergences
 * are recorded in ADR-0141.
 *
 * britbank-specific choices (ADR-0063/0102, kept and re-confirmed here): `apron`
 * draws **topmost among garments** (over dress/robe), and the shared `accessory`
 * slot (a hand-held lantern/torch/candle - UO OneHanded) draws **outermost, over
 * everything** including any facing of the cloak. The head layers stay above the
 * apron so a torso apron never occludes the face/hair.
 */
const BASE_ORDER: readonly RenderLayer[] = [
  // The mount (ADR-0442) is the bottom of the stack in every facing: UO composites a
  // ridden mobile by drawing the mount first and the rider over it, and unlike the
  // cloak there is no facing at which a mount passes in front of its rider (the rider
  // sits astride, so even facing north the near leg is still the rider's). Sitting
  // under `body` also puts it under the south-facing cloak, which is correct: a cloak
  // hangs behind the rider's back but still in front of the animal carrying them.
  'mount',
  'body',
  'pants',
  'feet',
  // Armor (the King's Justice guard kit, ADR-0194): plate legs over the trouser/boot
  // band, plate chest over the shirt band but UNDER doublet/sash/dress/robe/apron so
  // a cloth layer worn over the plate reads as draped over it (the guard's random
  // cloth layer); plate arms over the chest they strap onto.
  'armor-legs',
  'skirt',
  'shirt',
  'armor-chest',
  'armor-arms',
  'doublet',
  'sash',
  'dress',
  'robe',
  'apron',
  'hair',
  'beard',
  'headgear',
  // The in-the-hand weapon (halberd): over every garment and the head layers - a
  // gripped polearm reads in front of the figure - but still under the `accessory`
  // slot, preserving the ADR-0102 "held light outermost" invariant (nothing ever
  // carries both today).
  'weapon',
  'accessory',
];

/**
 * Where the `cloak` is spliced into {@link BASE_ORDER} for a facing - the single
 * facing-dependent move in UO's compositor. Confirmed against ClassicUO's
 * `ApplyDirectionCloak` (`Game/Data/PaperdollOrder.cs:231-267`, and the equivalent
 * legacy 8-direction table `Game/Data/LayerOrder.cs:7-64`):
 *
 *  - **South** (front, facing the viewer): the cloak hangs behind the body, so it
 *    is drawn **first of all** (index 0, under the body). ClassicUO dir 3.
 *  - **North** (back, facing away): the cloak hangs toward the viewer, drawn **on
 *    top** - here just under the always-outermost `accessory`, so a held light
 *    still reads over a back-turned cloak. ClassicUO dir 0 (cloak last).
 *  - **every other facing** (the six diagonals/sides): the cloak sits **just below
 *    the headgear** - over the body and garments, under the hat/helm. ClassicUO's
 *    "all other directions" case.
 *
 * Returns the index in {@link BASE_ORDER} the cloak is inserted *before* (so a
 * later index draws it further forward); {@link buildOrder} does the splice.
 */
function cloakInsertBefore(facing: Direction): RenderLayer {
  // South alone shows the pure front; the cloak drops behind the body.
  if (facing === Direction.S) return 'body';
  // North alone shows the pure back; the cloak swings over everything but the
  // outermost held accessory.
  if (facing === Direction.N) return 'accessory';
  // The six side/diagonal facings: cloak just under the headgear (over hair/beard).
  return 'headgear';
}

/** Splice `cloak` into {@link BASE_ORDER} just before `insertBefore`. */
function buildOrder(facing: Direction): readonly RenderLayer[] {
  const insertBefore = cloakInsertBefore(facing);
  const out: RenderLayer[] = [];
  for (const layer of BASE_ORDER) {
    if (layer === insertBefore) out.push('cloak');
    out.push(layer);
  }
  return out;
}

// Precompute the order per facing (pure data; the splice never changes at runtime).
const ORDER_BY_FACING = new Map<Direction, readonly RenderLayer[]>();
for (const facing of Object.values(Direction)) {
  if (typeof facing === 'number') ORDER_BY_FACING.set(facing, buildOrder(facing));
}

/**
 * The bottom-to-top {@link RenderLayer} draw order for a facing. A layer's index
 * in the returned list is its `zIndex` in the stack; a layer absent from the list
 * sorts to the bottom. Only the cloak's position changes between facings (UO's
 * single facing-dependent move), which is what keeps a cloaked character drawn
 * correctly as it turns. See {@link cloakInsertBefore}.
 */
export function layerOrderFor(facing: Direction): readonly RenderLayer[] {
  return ORDER_BY_FACING.get(facing) ?? buildOrder(Direction.S);
}

/**
 * The `zIndex` a layer takes within the stack for a facing: its position in
 * {@link layerOrderFor}, or `0` (bottom) if the slot is not in the table.
 */
export function layerZIndex(layer: RenderLayer, facing: Direction): number {
  const idx = layerOrderFor(facing).indexOf(layer);
  return idx < 0 ? 0 : idx;
}
